import { ChangeEvent, FormEvent, FunctionComponent, ReactElement, useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import PasswordField from '@/components/ui/form-inputs/PasswordField';
import LoadingButton from '@/components/ui/loading-button';
import { isValidPassword, requiredField } from '@/shared/utils/validation';
import { PASSWORDS_DO_NOT_MATCH, INVALID_PASSWORD } from '@/shared/utils/validation/constants';
import { useCompleteForcedPasswordChange } from '@/queries/auth';
import { CHANGE_PASSWORD_FORM_KEY } from '@/shared/constants/form-keys';
import AuthCardLayout from '../layouts/auth-card-layout/AuthCardLayout';
import AuthLayout from '../layouts/auth-layout/AuthLayout';

interface ForcedPasswordChangeFormProps {
	email: string;
	temporaryPassword: string;
	onBackToLogin: () => void;
}

const ForcedPasswordChangeForm: FunctionComponent<ForcedPasswordChangeFormProps> = ({
	email,
	temporaryPassword,
	onBackToLogin,
}): ReactElement => {
	const [newPassword, setNewPassword] = useState('');
	const [confirmNewPassword, setConfirmNewPassword] = useState('');
	const [newPasswordError, setNewPasswordError] = useState('');
	const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
	const [formError, setFormError] = useState('');

	const { isLoading, mutate: onCompleteForcedChange } = useCompleteForcedPasswordChange({
		onError: (message) => setFormError(message || 'Could not set your new password'),
	});

	const isNewPasswordValid = (): boolean => {
		if (newPassword.length === 0) {
			setNewPasswordError(requiredField('New password'));
			return false;
		}

		if (!isValidPassword(newPassword)) {
			setNewPasswordError(INVALID_PASSWORD);
			return false;
		}

		setNewPasswordError('');
		return true;
	};

	const isConfirmNewPasswordValid = (): boolean => {
		if (newPassword !== confirmNewPassword) {
			setConfirmNewPasswordError(PASSWORDS_DO_NOT_MATCH);
			return false;
		}

		if (confirmNewPassword.length === 0) {
			setConfirmNewPasswordError(requiredField('Confirm password'));
			return false;
		}

		setConfirmNewPasswordError('');
		return true;
	};

	const submit = (event: FormEvent) => {
		event.preventDefault();
		setFormError('');
		const newPasswordValid = isNewPasswordValid();
		const confirmNewPasswordValid = isConfirmNewPasswordValid();

		if (!newPasswordValid || !confirmNewPasswordValid) {
			return;
		}

		onCompleteForcedChange({
			request: {
				request: { email, currentPassword: temporaryPassword, newPassword },
				formKey: CHANGE_PASSWORD_FORM_KEY,
			},
		});
	};

	return (
		<Box component="section" role="main">
			<AuthLayout>
				<AuthCardLayout title="Set a new password">
					<Box component="form" onSubmit={submit} mt={3}>
						<Stack direction="column" spacing={3}>
							<Stack direction="column" spacing={2}>
								<Typography variant="body2">
									Your temporary password must be changed before you can log in. Choose a new
									password to continue.
								</Typography>
								{formError && (
									<Typography variant="body2" color="error">
										{formError}
									</Typography>
								)}
								<PasswordField
									id="newPassword"
									name="newPassword"
									label="New password"
									formKey={CHANGE_PASSWORD_FORM_KEY}
									fullWidth
									required
									value={newPassword}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setNewPassword(event.target.value)
									}
									clientErrors={newPasswordError}
								/>
								<PasswordField
									id="confirm-password"
									name="confirmPassword"
									label="Re-enter password"
									formKey={CHANGE_PASSWORD_FORM_KEY}
									fullWidth
									required
									value={confirmNewPassword}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setConfirmNewPassword(event.target.value)
									}
									clientErrors={confirmNewPasswordError}
								/>
								<Link component="button" type="button" onClick={onBackToLogin}>
									Back to log in
								</Link>
							</Stack>

							<LoadingButton
								loading={isLoading}
								variant="contained"
								size="large"
								fullWidth
								ariaLabel="Set new password progress"
								type="submit"
							>
								Set new password
							</LoadingButton>
						</Stack>
					</Box>
				</AuthCardLayout>
			</AuthLayout>
		</Box>
	);
};

export default ForcedPasswordChangeForm;
